import { Box, CircularProgress, IconButton, TextField, Tooltip, Button } from '@material-ui/core';
import { AttachFile, Refresh, Send, ExpandMore } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import ChatMessageBubble from './bubble';
import { CURRENT_USER } from './mock';
import api from './api';
import { Message } from './type';
import { Alert } from '@material-ui/lab';

interface ChatGroupProps {
    activeChatId: number | null;
    isOpen: boolean;
}

// Tempo de inatividade em milissegundos (padrão: 5 minutos)
const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

export default function ChatGroup({ activeChatId, isOpen, ...props }: ChatGroupProps) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [pendingMessages, setPendingMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [showLoadMore, setShowLoadMore] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    // Input State
    const [inputText, setInputText] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(Date.now());
    const isInitialLoadRef = useRef(true);

    // Reset inactivity timer
    const resetInactivityTimer = () => {
        lastActivityRef.current = Date.now();

        if (isPaused) {
            setIsPaused(false);
        }

        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        inactivityTimerRef.current = setTimeout(() => {
            setIsPaused(true);
        }, INACTIVITY_TIMEOUT);
    };

    // Start inactivity timer when chat opens
    useEffect(() => {
        if (activeChatId && isOpen) {
            resetInactivityTimer();
            // Reset states when switching chats
            setMessages([]);
            setHasMoreMessages(true);
            setShowLoadMore(false);
            isInitialLoadRef.current = true;
        } else {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
            setIsPaused(false);
        }

        return () => {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [activeChatId, isOpen]);

    // Polling for Messages (only when a chat is active)
    useEffect(() => {
        let interval: NodeJS.Timeout;

        const fetchMessages = async () => {
            if (!activeChatId || isPaused) return;
            try {
                const lastReadId = messages.length > 0 ? messages[messages.length - 1].id : undefined;
                const data = await api.getMessages(activeChatId, undefined, lastReadId);
                setMessages(data);

                // Se retornou menos que 20 mensagens na primeira carga, não há mais mensagens antigas
                if (isInitialLoadRef.current && data.length < 20) {
                    setHasMoreMessages(false);
                }
                isInitialLoadRef.current = false;
            } catch (e) {
                console.error("Failed to fetch messages", e);
            }
        };

        if (activeChatId && !isPaused) {
            setLoadingMessages(true);
            fetchMessages().then(() => setLoadingMessages(false));
            interval = setInterval(fetchMessages, 3000); // Poll messages faster (3s)
        }

        return () => clearInterval(interval);
    }, [activeChatId, isPaused]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, pendingMessages, activeChatId, isOpen]);

    // Detect scroll to top for loading older messages
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            // Show load more button when scrolled near the top (within 100px)
            if (scrollTop < 100 && hasMoreMessages && !loadingOlder) {
                setShowLoadMore(true);
            } else {
                setShowLoadMore(false);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasMoreMessages, loadingOlder]);

    const loadOlderMessages = async () => {
        if (!activeChatId || loadingOlder || !hasMoreMessages || messages.length === 0) return;

        setLoadingOlder(true);
        const firstMessageId = messages[0].id;
        const container = messagesContainerRef.current;
        const previousScrollHeight = container?.scrollHeight || 0;

        try {
            const lastReadId = messages[messages.length - 1].id;
            const olderMessages = await api.getMessages(activeChatId, firstMessageId, lastReadId);

            if (olderMessages.length === 0) {
                setHasMoreMessages(false);
            } else {
                // Adiciona as mensagens antigas no início
                setMessages(prev => [...olderMessages, ...prev]);

                // Mantém a posição do scroll após carregar novas mensagens
                setTimeout(() => {
                    if (container) {
                        const newScrollHeight = container.scrollHeight;
                        container.scrollTop = newScrollHeight - previousScrollHeight;
                    }
                }, 0);

                // Se retornou menos que 20, provavelmente não há mais
                if (olderMessages.length < 20) {
                    setHasMoreMessages(false);
                }
            }
        } catch (e) {
            console.error("Failed to load older messages", e);
        } finally {
            setLoadingOlder(false);
            setShowLoadMore(false);
        }
    };

    const handleSendMessage = async () => {
        if (!inputText.trim() || !activeChatId) return;

        resetInactivityTimer();

        const tempContent = inputText;
        setInputText("");

        // Create pending message
        const pendingMessage: Message = {
            id: Date.now(), // Temporary ID
            chat_id: activeChatId,
            sender_id: CURRENT_USER.id,
            content: tempContent,
            type: 'text',
            created_at: new Date().toISOString(),
            status: 'sending',
            self: true
        };

        setPendingMessages(prev => [...prev, pendingMessage]);

        try {
            await api.sendMessage(activeChatId, tempContent, 'text');
            const lastReadId = messages.length > 0 ? messages[messages.length - 1].id : undefined;
            const data = await api.getMessages(activeChatId, undefined, lastReadId);
            setMessages(data);
            setPendingMessages(prev => prev.filter(m => m.id !== pendingMessage.id));
        } catch (e) {
            console.error("Send failed", e);
            // Update pending message status to error
            setPendingMessages(prev =>
                prev.map(m => m.id === pendingMessage.id ? { ...m, status: 'error' } : m)
            );
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !activeChatId) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("File is too large. Max size is 5MB.");
            return;
        }

        // Create pending file message
        const pendingMessage: Message = {
            id: Date.now(),
            chat_id: activeChatId,
            sender_id: CURRENT_USER.id,
            content: file.name,
            type: 'file',
            file_size: file.size,
            created_at: new Date().toISOString(),
            status: 'sending',
            self: true
        };

        setPendingMessages(prev => [...prev, pendingMessage]);

        try {
            await api.sendMessage(activeChatId, file.name, 'file', file);
            const lastReadId = messages.length > 0 ? messages[messages.length - 1].id : undefined;
            const data = await api.getMessages(activeChatId, undefined, lastReadId);
            setMessages(data);
            setPendingMessages(prev => prev.filter(m => m.id !== pendingMessage.id));
        } catch (e) {
            console.error("File upload failed", e);
            setPendingMessages(prev =>
                prev.map(m => m.id === pendingMessage.id ? { ...m, status: 'error' } : m)
            );
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return <Box display="flex" flexDirection="column" height="100%">
        {/* Inactivity Alert */}
        {isPaused && (
            <Alert
                severity="info"
                action={
                    <IconButton
                        color="inherit"
                        size="small"
                        onClick={resetInactivityTimer}
                    >
                        <Refresh />
                    </IconButton>
                }
            >
                Chat pausado por inatividade. Clique para retomar ou comece a digitar.
            </Alert>
        )}

        {/* Messages Area */}
        <Box
            //@ts-ignore
            ref={messagesContainerRef}
            sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.50',
                position: 'relative'
            }}
        >
            {/* Load More Button */}
            {showLoadMore && hasMoreMessages && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2,
                        zIndex: 10
                    }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={loadingOlder ? <CircularProgress size={16} /> : <ExpandMore style={{ transform: 'rotate(180deg)' }} />}
                        onClick={loadOlderMessages}
                        disabled={loadingOlder}
                        style={{
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        {loadingOlder ? 'Carregando...' : 'Carregar mensagens antigas'}
                    </Button>
                </Box>
            )}

            {loadingMessages && messages.length === 0 ? <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
            </Box> : <>
                {messages.map((msg) => <ChatMessageBubble
                    key={msg.id}
                    message={msg}
                    isOwn={msg.self}
                />)}
                {pendingMessages.map((msg) => <ChatMessageBubble
                    key={msg.id}
                    message={msg}
                    isOwn={msg.self}
                />)}
            </>}
            <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
            sx={{
                p: 1.5,
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-end',
                gridGap: 1,
                flexShrink: 0
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
            <Tooltip title="Anexar arquivo">
                <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ marginBottom: '0.5rem' }}
                >
                    <AttachFile />
                </IconButton>
            </Tooltip>

            <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Digite sua mensagem..."
                variant="standard"
                value={inputText}
                onChange={(e) => {
                    setInputText(e.target.value);
                    resetInactivityTimer();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                    }
                }}
                onFocus={resetInactivityTimer}
                InputProps={{
                    disableUnderline: true,
                    style: {
                        fontSize: '0.9rem',
                        backgroundColor: 'whitesmoke',
                        borderRadius: '4rem',
                        paddingRight: '2rem',
                        paddingLeft: '2rem',
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                    }
                }}
            />

            <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
                style={{
                    marginBottom: '0.5rem',
                    transition: 'all 0.2s',
                    ...(inputText.trim() ? { transform: 'scale(1)' } : { transform: 'scale(0)', width: 0, p: 0 })
                }}
            >
                <Send />
            </IconButton>
        </Box>
    </Box>
}